import { createInertiaApp } from '@inertiajs/react'
import { createElement, ReactNode } from 'react'
import { createRoot } from 'react-dom/client'
import './application.css'
import AppLayout from '../layouts/AppLayout'
import AuthLayout from '../layouts/AuthLayout'
import { AppErrorBoundary } from '@/components/AppErrorBoundary'
import { applyAppearance, readInitialAppearance } from '@/lib/theme'
import { installStandaloneRecovery } from '@/lib/standalone-recovery'

// Apply the saved theme (dark class + primary/secondary CSS vars) before
// React mounts so there's no flash of the wrong colors on first paint.
applyAppearance(readInitialAppearance())

// Recover standalone (iOS Home Screen PWA) sessions from a discarded/suspended
// WebKit process. Client-only — this touches window/document.
installStandaloneRecovery()

// Temporary type definition, until @inertiajs/react provides one
type ResolvedComponent = {
  default: ReactNode & { layout?: (page: ReactNode) => ReactNode }
  layout?: (page: ReactNode) => ReactNode
}

createInertiaApp({
  title: title => title ? `${title} - Brain Down` : 'Brain Down',

  resolve: (name) => {
    const pages = import.meta.glob<ResolvedComponent>('../pages/**/*.tsx', {
      eager: true,
    })
    const page = pages[`../pages/${name}.tsx`]
    if (!page) {
      console.error(`Missing Inertia page component: '${name}.tsx'`)
    }

    // Auth pages use a minimal layout that still wires up the theme provider;
    // all other pages use the full AppLayout.
    if (name.startsWith('auth/')) {
      page.default.layout ||= (page: ReactNode) => createElement(AuthLayout, null, page)
    } else {
      page.default.layout ||= (page: ReactNode) => createElement(AppLayout, null, page)
    }

    return page
  },

  setup({ el, App, props }) {
    if (el) {
      createRoot(el).render(
        createElement(AppErrorBoundary, null, createElement(App, props)),
      )
    } else {
      console.error(
        'Missing root element.\n\n' +
          'If you see this error, it probably means you load Inertia.js on non-Inertia pages.\n' +
          'Consider moving <%= vite_typescript_tag "inertia" %> to the Inertia-specific layout instead.',
      )
    }
  },
})
